import { useState } from "react";
import { FileText, Mail, MapPin, Phone, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Chapter, FadeIn } from "./Reveal";
import { CONTACT, PRODUCTS } from "../data/site";

const EMPTY = { name: "", company: "", email: "", phone: "", product: "", message: "" };

const INFO = [
    { icon: MapPin, label: "Works & Office", value: CONTACT.address, testid: "contact-address" },
    {
        icon: Phone,
        label: "Phone / WhatsApp",
        value: CONTACT.phone,
        href: `tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`,
        testid: "contact-phone",
    },
    {
        icon: Mail,
        label: "Email",
        value: CONTACT.email,
        href: `mailto:${CONTACT.email}`,
        testid: "contact-email",
    },
    { icon: FileText, label: "GST No.", value: CONTACT.gst, testid: "contact-gst" },
    {
        icon: ShieldAlert,
        label: "Violations & Escalations — MD Direct",
        value: CONTACT.mdEmail,
        href: `mailto:${CONTACT.mdEmail}`,
        testid: "contact-md-email",
    },
];

const inputCls =
    "w-full rounded-none border-b border-line bg-transparent py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors focus:border-ember";

export const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/enquiries`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(form),
                },
            );
            if (!res.ok) throw new Error("send failed");
            toast.success("Enquiry sent. Our team will reach out shortly.");
            setForm(EMPTY);
        } catch {
            toast.error(
                "Could not send your enquiry. Please try again or email us directly.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="border-t border-line bg-coal/40 py-28 md:py-40"
        >
            <div className="mx-auto max-w-screen-2xl px-6 md:px-12">
                <Chapter number="06" label="Contact" />
                <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
                    <div>
                        <FadeIn>
                            <h2 className="font-display text-4xl uppercase leading-[0.95] tracking-tight text-zinc-100 md:text-6xl">
                                Let&apos;s build something that{" "}
                                <span className="text-ember">lasts</span>
                            </h2>
                            <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 md:text-base">
                                Send us your drawings, specs or volumes — our engineers
                                respond within one working day.
                            </p>
                        </FadeIn>
                        <div className="mt-12 space-y-8">
                            {INFO.map((item, i) => (
                                <FadeIn key={item.label} delay={0.1 + i * 0.06}>
                                    <div className="flex items-start gap-4" data-testid={item.testid}>
                                        <item.icon
                                            size={18}
                                            strokeWidth={1.5}
                                            className="mt-1 shrink-0 text-ember"
                                        />
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                                {item.label}
                                            </p>
                                            {item.href ? (
                                                <a
                                                    href={item.href}
                                                    data-testid={`${item.testid}-link`}
                                                    className="mt-1.5 block text-sm leading-relaxed text-zinc-200 transition-colors hover:text-ember md:text-base"
                                                >
                                                    {item.value}
                                                </a>
                                            ) : (
                                                <p className="mt-1.5 text-sm leading-relaxed text-zinc-200 md:text-base">
                                                    {item.value}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>

                    <FadeIn delay={0.15}>
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="space-y-7 border border-line bg-ink/60 p-8 backdrop-blur-sm md:p-10"
                        >
                            <div className="grid gap-7 sm:grid-cols-2">
                                <input
                                    data-testid="contact-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your name *"
                                    className={inputCls}
                                />
                                <input
                                    data-testid="contact-company-input"
                                    value={form.company}
                                    onChange={set("company")}
                                    placeholder="Company"
                                    className={inputCls}
                                />
                            </div>
                            <div className="grid gap-7 sm:grid-cols-2">
                                <input
                                    data-testid="contact-email-input"
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="Email *"
                                    className={inputCls}
                                />
                                <input
                                    data-testid="contact-phone-input"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="Phone"
                                    className={inputCls}
                                />
                            </div>
                            <select
                                data-testid="contact-product-select"
                                value={form.product}
                                onChange={set("product")}
                                className={`${inputCls} appearance-none bg-ink ${form.product ? "" : "text-zinc-600"}`}
                            >
                                <option value="" className="bg-ink text-zinc-400">
                                    Product of interest
                                </option>
                                {PRODUCTS.map((p) => (
                                    <option key={p.name} value={p.name} className="bg-ink text-zinc-200">
                                        {p.name}
                                    </option>
                                ))}
                                <option value="Other" className="bg-ink text-zinc-200">
                                    Other / Custom component
                                </option>
                            </select>
                            <textarea
                                data-testid="contact-message-input"
                                required
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                placeholder="Tell us about your requirement — drawings, volumes, timelines *"
                                className={`${inputCls} resize-none`}
                            />
                            <button
                                data-testid="contact-submit-btn"
                                type="submit"
                                disabled={sending}
                                className="w-full bg-ember px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors hover:bg-ember-hover disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {sending ? "Sending…" : "Send Enquiry"}
                            </button>
                        </form>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
};
